/**
 * Periodic Background Sync utilities
 * Enables automatic background data syncing every 12 hours
 */

export interface PeriodicSyncOptions {
  minInterval?: number; // in milliseconds (default: 12 hours)
}

const DEFAULT_INTERVAL = 12 * 60 * 60 * 1000; // 12 hours in ms

/**
 * Check if Periodic Background Sync is supported
 */
export function isPeriodicSyncSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'periodicSync' in ServiceWorkerRegistration.prototype
  );
}

/**
 * Register periodic background sync
 */
export async function registerPeriodicSync(
  options: PeriodicSyncOptions = {}
): Promise<boolean> {
  if (!isPeriodicSyncSupported()) {
    console.warn('Periodic Background Sync is not supported in this browser');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as any).periodicSync;

    // Request permission for periodic sync
    const status = await navigator.permissions.query({
      name: 'periodic-background-sync' as PermissionName,
    });

    if (status.state === 'denied') {
      console.warn('Periodic Background Sync permission denied');
      return false;
    }

    const minInterval = options.minInterval || DEFAULT_INTERVAL;

    // Register the periodic sync with tag
    await periodicSync.register('sync-data', {
      minInterval,
    });

    console.log('Periodic Background Sync registered successfully');
    return true;
  } catch (error) {
    console.error('Failed to register periodic background sync:', error);
    return false;
  }
}

/**
 * Unregister periodic background sync
 */
export async function unregisterPeriodicSync(): Promise<boolean> {
  if (!isPeriodicSyncSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as any).periodicSync;

    await periodicSync.unregister('sync-data');
    console.log('Periodic Background Sync unregistered');
    return true;
  } catch (error) {
    console.error('Failed to unregister periodic background sync:', error);
    return false;
  }
}

/**
 * Get list of registered periodic sync tags
 */
export async function getRegisteredPeriodicSyncs(): Promise<string[]> {
  if (!isPeriodicSyncSupported()) {
    return [];
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const periodicSync = (registration as any).periodicSync;

    const tags = await periodicSync.getTags();
    return tags;
  } catch (error) {
    console.error('Failed to get periodic sync tags:', error);
    return [];
  }
}

/**
 * Check if periodic sync is currently registered
 */
export async function isPeriodicSyncRegistered(): Promise<boolean> {
  const tags = await getRegisteredPeriodicSyncs();
  return tags.includes('sync-data');
}
